import React, { useEffect, useMemo, useState } from "react";
import type { CallbackEvent } from "@shopify/polaris-types";
import { useNavigate } from "react-router-dom";
import {
  useOffersListStore,
  selectHasPendingChanges,
  type OfferSummary,
} from "../../store/useOffersListStore";
import { useShopifySaveBar } from "../../hooks/useShopifySaveBar";
import { NavigationBlocker } from "../../components/common/NavigationBlocker";
import { PageBreadcrumbs } from "../../components/common/PageBreadcrumbs";
import { ListSearchBar } from "../../components/shared/ListSearchBar";
import { SalesBoosterTabBar } from "./SalesBoosterTabBar";
import { ListEmptySearchResults } from "../../components/shared/ListEmptySearchResults";

// ─── Sub-components ───────────────────────────────────────────────────────────

function UpsellsTable({
  upsells,
  allUpsellsCount,
  hasPendingChanges,
  searchQuery,
  onSearchChange,
  onEdit,
  onAdd,
  onToggleActive,
  onDelete,
  onDuplicate,
}: {
  upsells: OfferSummary[];
  allUpsellsCount: number;
  hasPendingChanges: boolean;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onEdit: (id: string) => void;
  onAdd: () => void;
  onToggleActive: (id: string, next: boolean) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
}) {
  // Empty state: no 1-tick offers exist on the server.
  if (allUpsellsCount === 0) {
    return (
      <s-box
        background="base"
        border="small subdued solid"
        borderRadius="large-100"
        padding="large-400"
      >
        <s-stack direction="block" gap="base" alignItems="center">
          <s-icon type="checkbox" />
          <s-heading>
            <strong>Crea tu primer upsell 1-tick</strong>
          </s-heading>
          <s-text color="subdued">
            Estos upsells aparecerán dentro de tu formulario COD y puede usarlos
            para aumentar tu AOV con complementos. Ejemplos:{" "}
            <strong>Protección de envío</strong>,{" "}
            <strong>Envoltorio de regalo</strong>,{" "}
            <strong>Garantía ampliada</strong>.
          </s-text>
          <s-button variant="primary" icon="plus" onClick={onAdd}>
            Añadir upsell
          </s-button>
        </s-stack>
      </s-box>
    );
  }

  // Empty state: every 1-tick offer is staged for deletion. Same visual
  // treatment as the true-empty state, different copy.
  if (upsells.length === 0 && !searchQuery && hasPendingChanges) {
    return (
      <s-box
        background="base"
        border="small subdued solid"
        borderRadius="large-100"
        padding="large-400"
      >
        <s-stack direction="block" gap="base" alignItems="center">
          <s-icon type="checkbox" />
          <s-text color="subdued">
            Todos los upsells están marcados para eliminar.
          </s-text>
          <s-text color="subdued">
            Guarda para confirmar o descarta los cambios para restaurarlos.
          </s-text>
        </s-stack>
      </s-box>
    );
  }

  return (
    <>
      <ListSearchBar value={searchQuery} onChange={onSearchChange} />

      {upsells.length === 0 ? (
        <ListEmptySearchResults onClear={() => onSearchChange("")}>
          Ningún upsell coincide con tu búsqueda.
        </ListEmptySearchResults>
      ) : (
        <s-table>
          <s-table-header-row>
            <s-table-header>Activo</s-table-header>
            <s-table-header>Nombre</s-table-header>
            <s-table-header>Acciones</s-table-header>
          </s-table-header-row>
          <s-table-body>
            {upsells.map((upsell) => (
              <s-table-row key={upsell.id}>
                <s-table-cell>
                  <s-switch
                    label="Activar upsell"
                    labelAccessibilityVisibility="exclusive"
                    checked={upsell.is_active}
                    onChange={(e: CallbackEvent<"s-switch">) =>
                      onToggleActive(upsell.id, e.currentTarget.checked)
                    }
                  />
                </s-table-cell>
                <s-table-cell>{upsell.internal_name}</s-table-cell>
                <s-table-cell>
                  <s-stack direction="inline" gap="small-200">
                    <s-button
                      variant="tertiary"
                      icon="edit"
                      onClick={() => onEdit(upsell.id)}
                    />
                    <s-button
                      variant="tertiary"
                      icon="duplicate"
                      onClick={() => onDuplicate(upsell.id)}
                    />
                    <s-button
                      variant="tertiary"
                      icon="delete"
                      tone="critical"
                      onClick={() => onDelete(upsell.id)}
                    />
                  </s-stack>
                </s-table-cell>
              </s-table-row>
            ))}
          </s-table-body>
        </s-table>
      )}
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const UpsellsOneTickPage: React.FC = () => {
  const navigate = useNavigate();

  // Read offers only when the store's last query was for *our* kind.
  // Otherwise (first paint after navigating from another kind page,
  // or any window where the store still holds another kind's rows) we
  // emit an empty list so the page doesn't briefly render foreign rows.
  const offers = useOffersListStore((s) =>
    s.query.kind === "one_tick" ? s.offers : []
  );
  const pendingDeleteIds = useOffersListStore((s) => s.pendingDeleteIds);
  const isLoading = useOffersListStore((s) => s.isLoading);
  const isCommitting = useOffersListStore((s) => s.isCommitting);
  const error = useOffersListStore((s) => s.error);
  // True when the store hasn't (yet) confirmed a fetch for our kind —
  // either nothing has loaded, or the most recent load was for another
  // page's kind. Drives the "show loader" branch below.
  const isAwaitingOurKind = useOffersListStore(
    (s) => s.query.kind !== "one_tick" || s.lastFetchedAt === null
  );
  const loadOffers = useOffersListStore((s) => s.loadOffers);
  const toggleActive = useOffersListStore((s) => s.toggleActive);
  const duplicateOffer = useOffersListStore((s) => s.duplicateOffer);
  const stageDelete = useOffersListStore((s) => s.stageDelete);
  const commitPending = useOffersListStore((s) => s.commitPending);
  const discardPending = useOffersListStore((s) => s.discardPending);

  const hasPendingChanges = useOffersListStore(selectHasPendingChanges);

  const visibleOffers = useMemo(() => {
    if (pendingDeleteIds.length === 0) return offers;
    const pending = new Set(pendingDeleteIds);
    return offers.filter((o) => !pending.has(o.id));
  }, [offers, pendingDeleteIds]);

  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    loadOffers({ kind: "one_tick" });
  }, [loadOffers]);

  const searchedOffers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return visibleOffers;
    return visibleOffers.filter((o) =>
      o.internal_name.toLowerCase().includes(q)
    );
  }, [visibleOffers, searchQuery]);

  const handleEdit = (id: string) =>
    navigate(`/sales-booster/upsells-1-tick/${id}`);
  const handleAdd = () => navigate("/sales-booster/upsells-1-tick/new");

  const handleToggleActive = async (id: string, next: boolean) => {
    try {
      await toggleActive(id, next);
      window.shopify?.toast?.show(
        next ? "Upsell activado" : "Upsell desactivado"
      );
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "No se pudo cambiar el estado del upsell";
      window.shopify?.toast?.show(message, { isError: true });
    }
  };

  const handleDelete = (id: string) => stageDelete(id);

  const handleDuplicate = async (id: string) => {
    try {
      await duplicateOffer(id);
      window.shopify?.toast?.show(
        "Upsell duplicado como borrador inactivo. Revísalo y actívalo cuando estés listo."
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "No se pudo duplicar el upsell";
      window.shopify?.toast?.show(message, { isError: true });
    }
  };

  useShopifySaveBar({
    isDirty: hasPendingChanges,
    isSaving: isCommitting,
    onSave: async () => {
      await commitPending();
    },
    onDiscard: discardPending,
  });

  const showInitialLoading = isLoading || isAwaitingOurKind;

  return (
    <div>
      <NavigationBlocker isDirty={hasPendingChanges} />

      <s-page heading="Upsells 1-Tick">
        <PageBreadcrumbs
          items={[{ label: "Impulsor de ventas", to: "/sales-booster" }]}
        />
        <s-stack direction="block" gap="large-200">
          <SalesBoosterTabBar />

          {error && !showInitialLoading && (
            <s-banner tone="critical">
              <s-text>{error}</s-text>
            </s-banner>
          )}
          <s-stack
            direction="inline"
            alignItems="center"
            justifyContent="space-between"
          >
            <s-box paddingBlockEnd="small-200">
              <s-heading>
                <strong>Tus upsells</strong>
              </s-heading>
            </s-box>
            <s-button variant="primary" icon="plus" onClick={handleAdd}>
              Añadir upsell
            </s-button>
          </s-stack>

          {showInitialLoading ? (
            <s-box
              background="base"
              border="small subdued solid"
              borderRadius="large-100"
              padding="large-400"
            >
              <s-stack direction="block" gap="base" alignItems="center">
                <s-text color="subdued">Cargando upsells…</s-text>
              </s-stack>
            </s-box>
          ) : (
            <s-section padding="none">
              <UpsellsTable
                upsells={searchedOffers}
                allUpsellsCount={offers.length}
                hasPendingChanges={hasPendingChanges}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onEdit={handleEdit}
                onAdd={handleAdd}
                onToggleActive={handleToggleActive}
                onDelete={handleDelete}
                onDuplicate={handleDuplicate}
              />
            </s-section>
          )}
        </s-stack>
      </s-page>
    </div>
  );
};

export default UpsellsOneTickPage;
